/**
 * VILLAGE EXTRAS — LAYOUT (pure arithmetic, no DOM, no three.js).
 *
 * Split from `village.ts` so `agent/bench-extras-village.mjs` can check every
 * piece against the SHIPPED collider boxes offline. This module has no
 * `mapIds` export, so the mapExtras registry loads it and skips it.
 *
 * What the village lacked after the generic passes (house windows, shopfronts,
 * plinth/cornice on the 6 m walls, kerbs on the cross paths, scanned trees):
 *  - the ONE-storey cottages (3 m walls) are below every generic dressing gate
 *    → bare boxes: they get a stone plinth, an eaves band and long/short corner
 *    QUOINS here;
 *  - every house shell has an open 0.2 x 0.2 notch at each outer corner (the
 *    shared `building()` stops each wall at the centre line) → filled;
 *  - doorways were holes → stone jambs/head/keystone, a threshold slab, the
 *    two plank door leaves folded back flat against the INSIDE wall, and a
 *    wrought-iron wall lantern;
 *  - downpipes with hopper heads, window flower boxes with geraniums, ivy;
 *  - the 70 m perimeter wall → stepped stone buttresses + ivy;
 *  - the plaza well → coping ring, footing and a plank lid; the crates →
 *    timber battens;
 *  - the sandy ground → grass tufts, mostly hugging wall bases.
 *
 * COLLISION CONTRACT (bench-asserted): everything is on a collider face and
 * ≤ 0.26 m proud of it, on a collider top ≤ 0.13 m above it, or is ground
 * foliage ≤ 0.45 m tall. Nothing enters a doorway, a spawn, or a jump pad, and
 * no piece overlaps what the generic passes already hang on the same walls
 * (windows, shopfronts, signs) — those are recomputed here with the very same
 * mapDressing functions the renderer calls.
 */
import type { MapBox, MapDef } from "@neon-purge/shared";
import {
  dressable, facadeSigns, houseWindowMap, houseWindows, paintedStreets, shellOutward, shopfront,
  signable, windowable, type DressTarget, type ShopPiece, type SignPiece, type WindowPiece,
} from "../mapDressing.js";

export type VMat = "stone" | "wood" | "iron" | "glass" | "leaf";
export type VShape = "box" | "cyl" | "card" | "tuft";
/** Leaf-atlas cells (see village.ts `leafAtlas`). */
export const CELL_IVY = 0, CELL_GRASS = 1, CELL_GERANIUM = 2, CELL_BLOOMS = 3;

export interface VPiece {
  kind: string;
  mat: VMat;
  shape: VShape;
  /** centre + FULL axis-aligned extents. A wall `card` has a ZERO extent on
   *  its facing axis (it is a plane); a `tuft` is a star of 3 vertical cards
   *  whose AABB is w x h x d; a `cyl` is Y-axis with radius w/2. */
  x: number; y: number; z: number;
  w: number; h: number; d: number;
  color: string;
  /** card / tuft: leaf-atlas cell */
  cell?: number;
  /** card: facing (axis-aligned unit) */
  nx?: number; nz?: number;
  /** tuft: rotation seed */
  rot?: number;
}

export interface AABB { x0: number; y0: number; z0: number; x1: number; y1: number; z1: number }
export const aabbOf = (p: { x: number; y: number; z: number; w: number; h: number; d: number }, pad = 0): AABB => ({
  x0: p.x - p.w / 2 - pad, x1: p.x + p.w / 2 + pad,
  y0: p.y - p.h / 2 - pad, y1: p.y + p.h / 2 + pad,
  z0: p.z - p.d / 2 - pad, z1: p.z + p.d / 2 + pad,
});
export const boxAABB = (b: MapBox): AABB => aabbOf({ x: b.p[0], y: b.p[1], z: b.p[2], w: b.s[0], h: b.s[1], d: b.s[2] });
/** Overlap depth: the SMALLEST per-axis overlap (≤ 0 → disjoint). */
export function overlapDepth(a: AABB, b: AABB): number {
  return Math.min(
    Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0),
    Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0),
    Math.min(a.z1, b.z1) - Math.max(a.z0, b.z0),
  );
}

/** Flat collider classes, as the renderer classifies them. */
export const isFlat = (b: MapBox): boolean => b.s[1] <= 1.2 && (b.s[0] >= 8 || b.s[2] >= 8);

/** What the generic passes put on the village's walls — windows, shopfronts,
 *  signs — recomputed with the renderer's own functions and gates. Returns the
 *  occupied AABBs and the window CILLS (flower boxes hang under them). */
export function genericDressing(map: MapDef, day: boolean, lowSpec: boolean): { occ: AABB[]; cills: WindowPiece[] } {
  const occ: AABB[] = [];
  const cills: WindowPiece[] = [];
  if (lowSpec) return { occ, cills }; // detailBudget is 0 there: none of it exists
  const boxes = map.boxes;
  // the same roof/upper-floor slabs the renderer gives shellOutward (capQueue)
  const caps: DressTarget[] = boxes.filter((b) => b.s[1] <= 1.2 && Math.max(b.s[0], b.s[2]) >= 3).map((b) => ({ p: b.p, s: b.s }));
  const shopWalls = new Set<MapBox>();
  const thin = (p: { w: number; h: number; d: number; x: number; y: number; z: number }) =>
    aabbOf({ ...p, w: Math.max(p.w, 0.02), d: Math.max(p.d, 0.02) });
  if (paintedStreets(map.id)) {
    // SUPERSET of the renderer's signQueue (it also needs `structural`, which
    // a dressable box always is) — over-reserving only costs us a piece.
    const signQ = boxes.filter((b) => !isFlat(b) && dressable(b.s[0], b.s[1], b.s[2]) && signable(b.s[0], b.s[1], b.s[2]));
    const targets: DressTarget[] = signQ.map((b) => ({ p: b.p, s: b.s }));
    const sp: SignPiece[] = [], shp: ShopPiece[] = [];
    for (let i = 0; i < signQ.length; i++) {
      sp.length = 0; shp.length = 0;
      const so = shellOutward(targets[i], targets, caps);
      facadeSigns(targets[i], true, sp, so);
      shopfront(targets[i], shp, so);
      if (shp.length) shopWalls.add(signQ[i]);
      for (const p of sp) occ.push(thin(p));
      for (const p of shp) occ.push(thin(p));
    }
  }
  if (day && houseWindowMap(map.id)) {
    const winQ = boxes.filter((b) => {
      const [w, h, d] = b.s;
      const building = h >= 5 && Math.min(w, d) >= 2.5;
      return !isFlat(b) && !building && !b.glow && !b.tag && windowable(w, h, d);
    });
    const targets: DressTarget[] = winQ.map((b) => ({ p: b.p, s: b.s }));
    const wp: WindowPiece[] = [];
    for (let i = 0; i < winQ.length; i++) {
      wp.length = 0;
      houseWindows(targets[i], shellOutward(targets[i], targets, caps), true, wp, shopWalls.has(winQ[i]) ? 1 : 0);
      for (const p of wp) {
        occ.push(thin(p));
        if (p.kind === "cill") cills.push(p);
      }
    }
  }
  return { occ, cills };
}

/** Deterministic hash in [0,1) (same on every client, every round). */
export function hash01(a: number, b: number, salt: number): number {
  const s = Math.sin(a * 127.1 + b * 311.7 + salt * 74.7) * 43758.5453;
  return s - Math.floor(s);
}
function rng(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/** A face of a wall: outward normal (nx,nz), the plane coordinate `c` on the
 *  normal axis, the tangent span [a0,a1], and the floor/top heights. */
interface Face { nx: number; nz: number; c: number; a0: number; a1: number; y0: number; y1: number }

/** A box on a face: centred at tangent `a`, `aw` wide, from height y0..y1,
 *  occupying proud distances p0..p1 off the face (p0 < 0 embeds). */
function onFace(f: Face, a: number, aw: number, y0: number, y1: number, p0: number, p1: number): Omit<VPiece, "kind" | "mat" | "color"> {
  const mid = f.c + (f.nx || f.nz) * (p0 + p1) / 2;
  const t = p1 - p0;
  return f.nz !== 0
    ? { shape: "box", x: a, y: (y0 + y1) / 2, z: mid, w: aw, h: y1 - y0, d: t }
    : { shape: "box", x: mid, y: (y0 + y1) / 2, z: a, w: t, h: y1 - y0, d: aw };
}
/** A plane card on a face, `p` proud. */
function cardOn(f: Face, a: number, aw: number, y0: number, y1: number, p: number, cell: number): Omit<VPiece, "kind" | "mat" | "color"> {
  const n = f.c + (f.nx || f.nz) * p;
  return f.nz !== 0
    ? { shape: "card", x: a, y: (y0 + y1) / 2, z: n, w: aw, h: y1 - y0, d: 0, cell, nx: 0, nz: f.nz }
    : { shape: "card", x: n, y: (y0 + y1) / 2, z: a, w: 0, h: y1 - y0, d: aw, cell, nx: f.nx, nz: 0 };
}

export interface VillageOpts { lowSpec: boolean; day: boolean }

/** Piece colours (multiplied into the surface textures). */
const C = {
  quoin: "#bcab8b", stone: "#ad9d80", stoneDk: "#8a7c64", wellStone: "#9a988c",
  door: "#6a4628", doorAlt: "#4a5c44", crate: "#8a6a44", plank: "#a5845a", box: "#6e5034",
  iron: "#2a2826", ivy: "#ffffff", grass: "#ffffff", glass: "#ffe7b5",
};

/** Every extras piece for a village-like map. Pure + deterministic. */
export function villageLayout(map: MapDef, opts: VillageOpts): VPiece[] {
  const boxes = map.boxes;
  const solids = boxes.map(boxAABB);
  const { occ, cills } = genericDressing(map, opts.day, opts.lowSpec);
  /** what ivy may never cover: the generic passes + our own hardware */
  const noIvy: AABB[] = occ.slice();
  const HARDWARE = new Set(["pipe", "hopper", "shoe", "clip", "lantern", "jamb", "doorhead", "keystone"]);
  const out: VPiece[] = [];

  /** Highest floor top (flat box ≤ 0.5 m) under a point. */
  const floorTop = (x: number, z: number): number => {
    let t = -Infinity;
    for (const b of boxes) {
      if (!isFlat(b)) continue;
      const top = b.p[1] + b.s[1] / 2;
      if (top > 0.5) continue;
      if (Math.abs(x - b.p[0]) <= b.s[0] / 2 && Math.abs(z - b.p[2]) <= b.s[2] / 2) t = Math.max(t, top);
    }
    return t === -Infinity ? 0 : t;
  };

  /** Add a group all-or-nothing: rejected if any (non-plant) piece hits what
   *  is already on the wall, or pushes > 3 cm into a collider. `embedOk`
   *  boxes are allowed to be overlapped (a crate's own batten posts). */
  const tryGroup = (g: VPiece[], opt: { ignoreOcc?: boolean; embedOk?: AABB } = {}): boolean => {
    for (const p of g) {
      const a = aabbOf(p);
      const plant = p.shape === "card" && p.cell !== CELL_IVY;
      if (!opt.ignoreOcc && !plant) for (const o of occ) if (overlapDepth(a, o) > 0.012) return false;
      for (const s of solids) {
        if (s === opt.embedOk) continue;
        if (overlapDepth(a, s) > 0.03) return false;
      }
    }
    for (const p of g) {
      out.push(p);
      if (p.shape !== "card") occ.push(aabbOf(p));
      if (HARDWARE.has(p.kind)) noIvy.push(aabbOf(p));
    }
    return true;
  };
  const P = (kind: string, mat: VMat, color: string, geo: Omit<VPiece, "kind" | "mat" | "color">): VPiece => ({ kind, mat, color, ...geo });

  // ---------------------------------------------------------------- HOUSES
  // A house = an elevated 0.3 m roof slab with its back wall under it (the
  // shared `building()` shape). Found from the boxes, not hard-coded.
  const houses: { cx: number; cz: number; w: number; d: number; H: number; T: number; ft: number; col: string }[] = [];
  for (const b of boxes) {
    if (Math.abs(b.s[1] - 0.3) > 1e-6 || b.s[0] < 6 || b.s[2] < 6 || b.p[1] < 2) continue;
    const cx = b.p[0], cz = b.p[2], w = b.s[0], d = b.s[2], H = b.p[1] - 0.15;
    const back = boxes.find((o) => Math.abs(o.p[0] - cx) < 1e-6 && Math.abs(o.p[2] - (cz + d / 2)) < 1e-6
      && Math.abs(o.s[0] - w) < 1e-6 && Math.abs(o.s[1] - H) < 1e-6);
    if (!back) continue;
    houses.push({ cx, cz, w, d, H, T: back.s[2], ft: floorTop(cx, cz - d / 2), col: back.color ?? "#c9b79a" });
  }

  for (const hs of houses) {
    const { cx, cz, w, d, H, T, ft } = hs;
    const t = T / 2;
    const xL = cx - w / 2 - t, xR = cx + w / 2 + t, zF = cz - d / 2 - t, zB = cz + d / 2 + t;
    const faces: Record<"back" | "front" | "left" | "right", Face> = {
      back: { nx: 0, nz: 1, c: zB, a0: xL, a1: xR, y0: ft, y1: H },
      front: { nx: 0, nz: -1, c: zF, a0: xL, a1: xR, y0: ft, y1: H },
      left: { nx: -1, nz: 0, c: xL, a0: zF, a1: zB, y0: ft, y1: H },
      right: { nx: 1, nz: 0, c: xR, a0: zF, a1: zB, y0: ft, y1: H },
    };
    const hh = hash01(cx, cz, 1);
    const cottage = H < 3.2; // below every generic dressing gate (dressable wants 3.2 m)

    // CORNER NOTCHES — each wall stops at the other's centre line, leaving a
    // T/2-square hole up every outer corner. Fill with dressed stone.
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
      tryGroup([P("notch", "stone", C.quoin, {
        shape: "box", x: cx + sx * (w / 2 + t / 2), y: (ft + H) / 2, z: cz + sz * (d / 2 + t / 2),
        w: t, h: H - ft, d: t,
      })], { ignoreOcc: true });
    }

    // DOORWAY — stone jambs + head + keystone, threshold, folded-back leaves.
    {
      const f = faces.front;
      const DW = 1.0, DH = 2.4, JW = 0.22;
      tryGroup([
        P("jamb", "stone", C.stone, onFace(f, cx - DW - JW / 2, JW, ft, DH, -0.005, 0.08)),
        P("jamb", "stone", C.stone, onFace(f, cx + DW + JW / 2, JW, ft, DH, -0.005, 0.08)),
        P("doorhead", "stone", C.stone, onFace(f, cx, 2 * (DW + JW), DH, DH + 0.26, -0.005, 0.1)),
        P("keystone", "stone", C.quoin, onFace(f, cx, 0.26, DH, DH + 0.36, -0.005, 0.13)),
      ]);
      // threshold: in the gap, across the wall and 0.25 m onto the street
      tryGroup([P("threshold", "stone", C.stoneDk, {
        shape: "box", x: cx, y: ft + 0.0175, z: (zF - 0.25 + (cz - d / 2 + t)) / 2,
        w: 2 * DW - 0.04, h: 0.035, d: (cz - d / 2 + t) - (zF - 0.25),
      })], { ignoreOcc: true });
      // leaves: opened 180° flat against the INSIDE of the front wall
      const fin: Face = { nx: 0, nz: 1, c: cz - d / 2 + t, a0: cx - w / 2, a1: cx + w / 2, y0: ft, y1: H };
      const leafCol = hh < 0.5 ? C.door : C.doorAlt;
      for (const s of [-1, 1]) {
        const a = cx + s * (DW + 0.03 + 0.47);
        const hinge = cx + s * (DW + 0.03 + 0.3);
        tryGroup([
          P("leaf", "wood", leafCol, onFace(fin, a, 0.94, ft + 0.02, DH - 0.04, 0.004, 0.055)),
          P("hinge", "iron", C.iron, onFace(fin, hinge, 0.55, ft + 0.42, ft + 0.47, 0.055, 0.067)),
          P("hinge", "iron", C.iron, onFace(fin, hinge, 0.55, DH - 0.5, DH - 0.45, 0.055, 0.067)),
        ]);
      }
      // WALL LANTERN — beside the door, on the side the hash picks
      for (const s of hh < 0.5 ? [1, -1] : [-1, 1]) {
        const a = cx + s * (DW + JW + 0.26);
        if (tryGroup([
          P("lantern", "iron", C.iron, onFace(f, a, 0.1, 2.42, 2.62, -0.005, 0.02)),     // wall plate
          P("lantern", "iron", C.iron, onFace(f, a, 0.03, 2.57, 2.6, 0.02, 0.245)),       // arm
          P("lantern", "iron", C.iron, onFace(f, a, 0.19, 2.5, 2.545, 0.07, 0.255)),     // cap
          P("lantern", "glass", C.glass, onFace(f, a, 0.13, 2.3, 2.5, 0.1, 0.23)),       // lit glass
          P("lantern", "iron", C.iron, onFace(f, a, 0.15, 2.27, 2.3, 0.09, 0.24)),       // base
        ])) break;
      }
    }

    if (cottage) {
      // PLINTH — 0.42 m stone base course, 7 cm proud; split at the door.
      const PL = 0.42, PP = 0.07;
      for (const k of ["back", "left", "right"] as const) {
        const f = faces[k];
        const wrap = f.nz !== 0 ? PP : 0;
        tryGroup([P("plinth", "stone", C.stoneDk, onFace(f, (f.a0 + f.a1) / 2, f.a1 - f.a0 + 2 * wrap, ft, ft + PL, -0.005, PP))], { ignoreOcc: true });
      }
      {
        const f = faces.front;
        const l0 = f.a0 - PP, l1 = cx - 1.22, r0 = cx + 1.22, r1 = f.a1 + PP; // stops at the jambs
        tryGroup([
          P("plinth", "stone", C.stoneDk, onFace(f, (l0 + l1) / 2, l1 - l0, ft, ft + PL, -0.005, PP)),
          P("plinth", "stone", C.stoneDk, onFace(f, (r0 + r1) / 2, r1 - r0, ft, ft + PL, -0.005, PP)),
        ], { ignoreOcc: true });
      }
      // EAVES BAND — the stone course under the roof line.
      const EB = 0.22, EP = 0.1;
      for (const k of ["back", "front", "left", "right"] as const) {
        const f = faces[k];
        const wrap = f.nz !== 0 ? EP : 0;
        tryGroup([P("eaves", "stone", C.stone, onFace(f, (f.a0 + f.a1) / 2, f.a1 - f.a0 + 2 * wrap, H - EB - 0.02, H - 0.02, -0.005, EP))], { ignoreOcc: true });
      }
      // QUOINS — alternating long/short dressed blocks up every corner.
      if (!opts.lowSpec) {
        const QP = 0.045, QH = 0.28, GAP = 0.03;
        const y0 = ft + PL + 0.02, y1 = H - EB - 0.04;
        const n = Math.floor((y1 - y0 + GAP) / (QH + GAP));
        for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
          const ex = sx > 0 ? xR : xL, ez = sz > 0 ? zB : zF;
          for (let i = 0; i < n; i++) {
            const by0 = y0 + i * (QH + GAP), by1 = by0 + QH;
            const la = i % 2 ? 0.55 : 0.32, lb = i % 2 ? 0.32 : 0.55;
            // leg on the ±Z face (wraps the corner), leg on the ±X face
            const xa0 = sx > 0 ? ex - la : ex - QP, xa1 = sx > 0 ? ex + QP : ex + la;
            const za0 = sz > 0 ? ez - lb : ez, za1 = sz > 0 ? ez : ez + lb;
            const zz0 = sz > 0 ? ez - 0.005 : ez - QP, zz1 = sz > 0 ? ez + QP : ez + 0.005;
            const xx0 = sx > 0 ? ex - 0.005 : ex - QP, xx1 = sx > 0 ? ex + QP : ex + 0.005;
            tryGroup([
              P("quoin", "stone", C.quoin, { shape: "box", x: (xa0 + xa1) / 2, y: (by0 + by1) / 2, z: (zz0 + zz1) / 2, w: xa1 - xa0, h: QH, d: zz1 - zz0 }),
              P("quoin", "stone", C.quoin, { shape: "box", x: (xx0 + xx1) / 2, y: (by0 + by1) / 2, z: (za0 + za1) / 2, w: xx1 - xx0, h: QH, d: za1 - za0 }),
            ]);
          }
        }
      }
    }

    // DOWNPIPE — hopper head under the eaves, pipe, clips, kicked-out shoe.
    {
      const top = cottage ? H - 0.26 : H - 0.32;
      const cands: [Face, number][] = [
        [faces.back, hh < 0.5 ? xR - 0.72 : xL + 0.72], [faces.back, hh < 0.5 ? xL + 0.72 : xR - 0.72],
        [faces.left, zB - 0.72], [faces.right, zB - 0.72], [faces.left, zF + 0.72], [faces.right, zF + 0.72],
        [faces.front, hh < 0.5 ? xR - 0.72 : xL + 0.72], [faces.front, hh < 0.5 ? xL + 0.72 : xR - 0.72],
      ];
      for (const [f, a] of cands) {
        const n = f.nx || f.nz;
        const pipeC = f.c + n * 0.12;
        const r = 0.045;
        const y0 = ft + 0.16, y1 = top - 0.2;
        const pipe: VPiece = f.nz !== 0
          ? P("pipe", "iron", C.iron, { shape: "cyl", x: a, y: (y0 + y1) / 2, z: pipeC, w: 2 * r, h: y1 - y0, d: 2 * r })
          : P("pipe", "iron", C.iron, { shape: "cyl", x: pipeC, y: (y0 + y1) / 2, z: a, w: 2 * r, h: y1 - y0, d: 2 * r });
        const g: VPiece[] = [
          pipe,
          P("hopper", "iron", C.iron, onFace(f, a, 0.24, top - 0.2, top, 0.02, 0.22)),
          P("shoe", "iron", C.iron, onFace(f, a, 0.1, ft, ft + 0.16, 0.07, 0.25)),
        ];
        for (const k of [0.33, 0.66]) {
          const yc = y0 + (y1 - y0) * k;
          g.push(P("clip", "iron", C.iron, onFace(f, a, 0.11, yc - 0.02, yc + 0.02, -0.005, 0.17)));
        }
        if (tryGroup(g)) break;
      }
    }

    // IVY — one climbing patch on about half the houses, at a corner
    if (hh > 0.45) {
      const cands: [Face, number][] = [
        [faces.left, zB - 0.8], [faces.right, zB - 0.8], [faces.back, xL + 0.8], [faces.back, xR - 0.8],
        [faces.left, zF + 0.8], [faces.right, zF + 0.8],
      ];
      const start = Math.floor(hash01(cx, cz, 2) * cands.length);
      for (let i = 0; i < cands.length; i++) {
        const [f, a] = cands[(start + i) % cands.length];
        const hTop = Math.min(H - 0.3, ft + 1.6 + hash01(cx, cz, 3) * (H - 1.8));
        const g = ivyPatch(f, a, 1.25, ft, hTop, hash01(cx, cz, 4), opts.lowSpec, P);
        // an ivy patch may not cover glass or a shop: check its footprint as one box
        const foot = P("ivy-foot", "leaf", "", onFace(f, a, 1.3, ft, hTop, 0, 0.16));
        const fa = aabbOf(foot);
        if (noIvy.some((o) => overlapDepth(fa, o) > 0.005)) continue;
        if (tryGroup(g, { ignoreOcc: true })) break;
      }
    }
  }

  // ---------------------------------------------------- WINDOW FLOWER BOXES
  for (const c of cills) {
    if (hash01(c.x, c.z + c.y, 5) > 0.62) continue;
    // the wall face the cill hangs on
    const f: Face = c.nz !== 0
      ? { nx: 0, nz: c.nz, c: c.z - c.nz * c.d / 2, a0: 0, a1: 0, y0: 0, y1: 0 }
      : { nx: c.nx, nz: 0, c: c.x - c.nx * c.w / 2, a0: 0, a1: 0, y0: 0, y1: 0 };
    const a = c.nz !== 0 ? c.x : c.z;
    const len = (c.nz !== 0 ? c.w : c.d) - 0.14;
    const top = c.y - c.h / 2 - 0.012;
    const bloom = hash01(c.x, c.z, 6) < 0.6 ? CELL_GERANIUM : CELL_BLOOMS;
    tryGroup([
      P("flowerbox", "wood", hash01(c.x, c.z, 8) < 0.5 ? C.box : "#4b5d3f", onFace(f, a, len, top - 0.2, top, -0.005, 0.2)),
      P("flowers", "leaf", "#ffffff", cardOn(f, a, len + 0.06, top - 0.12, top + 0.3, 0.075, bloom)),
      P("flowers", "leaf", "#e8f0e0", cardOn(f, a + 0.03, len - 0.04, top - 0.16, top + 0.22, 0.165, bloom)),
    ], { ignoreOcc: false });
  }

  // ------------------------------------------------------- PERIMETER WALL
  for (const b of boxes) {
    const [w, h, d] = b.s;
    const long = Math.max(w, d), thick = Math.min(w, d);
    if (!(h >= 4 && long >= 40 && thick <= 1.5)) continue;
    const alongX = w > d;
    const n = alongX ? -Math.sign(b.p[2] || 1) : -Math.sign(b.p[0] || 1); // toward the centre
    const top = b.p[1] + h / 2;
    const half = long / 2 - thick; // clear of the crossing walls
    const ac = alongX ? b.p[0] : b.p[2];
    const f: Face = alongX
      ? { nx: 0, nz: n, c: b.p[2] + n * thick / 2, a0: ac - half, a1: ac + half, y0: 0, y1: top }
      : { nx: n, nz: 0, c: b.p[0] + n * thick / 2, a0: ac - half, a1: ac + half, y0: 0, y1: top };
    const ftAt = (a: number): number => floorTop(alongX ? a : f.c + n * 0.5, alongX ? f.c + n * 0.5 : a);
    // BUTTRESSES — two-tier stepped stone piers every 7 m, clear of the paths
    for (let a = ac - half + 6; a <= ac + half - 6 + 1e-6; a += 7) {
      if (Math.abs(a) < 4.6) continue;
      const ft = ftAt(a);
      tryGroup([
        P("buttress", "stone", C.stoneDk, onFace(f, a, 0.78, ft, ft + 2.3, -0.005, 0.22)),
        P("buttress", "stone", C.stone, onFace(f, a, 0.86, ft + 2.3, ft + 2.38, -0.005, 0.255)),
        P("buttress", "stone", C.stoneDk, onFace(f, a, 0.6, ft + 2.38, ft + 3.5, -0.005, 0.14)),
        P("buttress", "stone", C.stone, onFace(f, a, 0.68, ft + 3.5, ft + 3.57, -0.005, 0.175)),
      ]);
    }
    // IVY between the buttresses
    for (let a = ac - half + 2.5; a <= ac + half - 2.5 + 1e-6; a += 7) {
      if (Math.abs(a) < 4.8) continue;
      const hv = hash01(a, b.p[0] + b.p[2], 9);
      const ft = ftAt(a);
      if (hv > (opts.lowSpec ? 0.3 : 0.55)) continue;
      const hTop = Math.min(top - 0.12, ft + 2.0 + hash01(a, b.p[2], 10) * 2.0);
      tryGroup(ivyPatch(f, a, 2.2 + hv * 2.2, ft, hTop, hash01(a, b.p[0], 11), opts.lowSpec, P), { ignoreOcc: true });
    }
  }

  // ----------------------------------------------------- WELL + CRATES
  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i];
    const [w, h, d] = b.s;
    const bot = b.p[1] - h / 2, top = b.p[1] + h / 2;
    if (bot > 0.1 || isFlat(b)) continue;
    // WELL: ~3 m square, chest-high
    if (w >= 2.4 && w <= 3.6 && Math.abs(w - d) < 0.01 && h >= 1.1 && h <= 1.9) {
      const lip = 0.05, band = 0.32, ch = 0.09;
      const x0 = b.p[0], z0 = b.p[2];
      const g: VPiece[] = [
        P("coping", "stone", C.wellStone, { shape: "box", x: x0, y: top + ch / 2 - 0.01, z: z0 - d / 2 + band / 2 - lip / 2, w: w + 2 * lip, h: ch, d: band + lip }),
        P("coping", "stone", C.wellStone, { shape: "box", x: x0, y: top + ch / 2 - 0.01, z: z0 + d / 2 - band / 2 + lip / 2, w: w + 2 * lip, h: ch, d: band + lip }),
        P("coping", "stone", C.wellStone, { shape: "box", x: x0 - w / 2 + band / 2 - lip / 2, y: top + ch / 2 - 0.01, z: z0, w: band + lip, h: ch, d: d - 2 * band }),
        P("coping", "stone", C.wellStone, { shape: "box", x: x0 + w / 2 - band / 2 + lip / 2, y: top + ch / 2 - 0.01, z: z0, w: band + lip, h: ch, d: d - 2 * band }),
      ];
      // footing band hugging the base
      const fH = 0.24, fl = 0.06;
      g.push(
        P("footing", "stone", C.stoneDk, { shape: "box", x: x0, y: fH / 2 + 0.005, z: z0 - d / 2 - fl / 2 + 0.005, w: w + 2 * fl, h: fH, d: fl }),
        P("footing", "stone", C.stoneDk, { shape: "box", x: x0, y: fH / 2 + 0.005, z: z0 + d / 2 + fl / 2 - 0.005, w: w + 2 * fl, h: fH, d: fl }),
        P("footing", "stone", C.stoneDk, { shape: "box", x: x0 - w / 2 - fl / 2 + 0.005, y: fH / 2 + 0.005, z: z0, w: fl, h: fH, d }),
        P("footing", "stone", C.stoneDk, { shape: "box", x: x0 + w / 2 + fl / 2 - 0.005, y: fH / 2 + 0.005, z: z0, w: fl, h: fH, d }),
      );
      // plank LID inside the ring + two battens across it
      const inner = w - 2 * band, pw = 0.3, np = Math.floor(inner / (pw + 0.02));
      const span = np * (pw + 0.02) - 0.02;
      for (let k = 0; k < np; k++) {
        const px = x0 - span / 2 + pw / 2 + k * (pw + 0.02);
        g.push(P("lid", "wood", k % 2 ? C.plank : C.crate, { shape: "box", x: px, y: top + 0.02 - 0.005, z: z0, w: pw, h: 0.04, d: inner }));
      }
      for (const s of [-1, 1]) {
        g.push(P("lid", "wood", C.box, { shape: "box", x: x0, y: top + 0.055, z: z0 + s * inner * 0.28, w: span, h: 0.035, d: 0.12 }));
      }
      tryGroup(g, { embedOk: solids[i] });
      continue;
    }
    // CRATE: a cube-ish box 0.9–2 m, ≤ 1.15 m tall, on the floor (the grey 2.2 m
    // stone cover blocks are NOT crates)
    if (Math.max(w, d) <= 2.0 && Math.min(w, d) >= 0.9 && h >= 0.6 && h <= 1.15 && Math.abs(w - d) < 0.3 && b.color !== "#7a7f88") {
      const x0 = b.p[0], z0 = b.p[2];
      const PO = 0.025, S = 0.11; // proud, batten section
      const g: VPiece[] = [];
      // PLANK SKIN: the collider wears the map's stone texture, so a crate read
      // as a stone block — clad its four sides and lid in boards (2 cm)
      const PS = 0.015;
      for (const s of [-1, 1]) {
        g.push(P("panel", "wood", C.plank, { shape: "box", x: x0, y: (bot + top) / 2, z: z0 + s * (d / 2 + PS / 2), w, h, d: PS }));
        g.push(P("panel", "wood", C.plank, { shape: "box", x: x0 + s * (w / 2 + PS / 2), y: (bot + top) / 2, z: z0, w: PS, h, d }));
      }
      g.push(P("panel", "wood", C.plank, { shape: "box", x: x0, y: top + PS / 2, z: z0, w, h: PS, d }));
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
        g.push(P("batten", "wood", C.box, {
          shape: "box", x: x0 + sx * (w / 2 + PO - S / 2), y: (bot + top) / 2, z: z0 + sz * (d / 2 + PO - S / 2), w: S, h, d: S,
        }));
      }
      for (const yy of [bot + S / 2, top - S / 2, (bot + top) / 2]) {
        for (const s of [-1, 1]) {
          g.push(P("batten", "wood", C.crate, { shape: "box", x: x0, y: yy, z: z0 + s * (d / 2 + PO / 2), w: w - 2 * (S - PO), h: S, d: PO }));
          g.push(P("batten", "wood", C.crate, { shape: "box", x: x0 + s * (w / 2 + PO / 2), y: yy, z: z0, w: PO, h: S, d: d - 2 * (S - PO) }));
        }
      }
      tryGroup(g, { ignoreOcc: true, embedOk: solids[i] });
    }
  }

  // ---------------------------------------------------------- GRASS TUFTS
  {
    const ground = boxes.filter((b) => isFlat(b) && b.p[1] + b.s[1] / 2 <= 0.05);
    const big = ground.reduce<MapBox | null>((m, b) => (!m || b.s[0] * b.s[2] > m.s[0] * m.s[2] ? b : m), null);
    if (big) {
      const gTop = big.p[1] + big.s[1] / 2;
      const raised = ground.filter((b) => b !== big); // the paved cross paths
      const tall = boxes.filter((b) => !isFlat(b) && b.p[1] - b.s[1] / 2 < 0.5);
      const keepOut = [
        ...map.spawns.map((s) => ({ x: s.p[0], z: s.p[2], r: 1.4 })),
        ...(map.jumpPads ?? []).map((j) => ({ x: j.p[0], z: j.p[2], r: 1.8 })),
      ];
      const want = opts.lowSpec ? 90 : 280;
      const rnd = rng(0x5ea11a9e);
      const hx = big.s[0] / 2 - 1.1, hz = big.s[2] / 2 - 1.1;
      let placed = 0;
      for (let tries = 0; tries < want * 30 && placed < want; tries++) {
        const x = big.p[0] + (rnd() * 2 - 1) * hx, z = big.p[2] + (rnd() * 2 - 1) * hz;
        const accept = rnd(), sz = rnd(), sh = rnd(), sr = rnd();
        if (raised.some((b) => Math.abs(x - b.p[0]) < b.s[0] / 2 + 0.45 && Math.abs(z - b.p[2]) < b.s[2] / 2 + 0.45)) continue;
        if (keepOut.some((k) => Math.hypot(x - k.x, z - k.z) < k.r)) continue;
        // inside a house, or in front of its door
        if (houses.some((hs) => Math.abs(x - hs.cx) < hs.w / 2 + 0.3 && Math.abs(z - hs.cz) < hs.d / 2 + 0.3)) continue;
        if (houses.some((hs) => Math.abs(x - hs.cx) < 1.6 && z < hs.cz - hs.d / 2 && z > hs.cz - hs.d / 2 - 2.6)) continue;
        // distance to the nearest standing collider footprint
        let dw = Infinity;
        for (const b of tall) {
          const ex = Math.max(Math.abs(x - b.p[0]) - b.s[0] / 2, 0), ez = Math.max(Math.abs(z - b.p[2]) - b.s[2] / 2, 0);
          dw = Math.min(dw, Math.max(ex, ez)); // Chebyshev: the tuft's square AABB clears it iff dw > s/2
        }
        const nearWall = dw < 1.1;
        if (accept > (nearWall ? 0.85 : 0.035)) continue;
        const s = (nearWall ? 0.36 : 0.3) + sz * 0.2;
        if (dw < s / 2 + 0.03) continue; // never into a collider (buttress blades are fine)
        const th = Math.min(0.44, (nearWall ? 0.3 : 0.22) + sh * 0.14);
        out.push({ kind: "tuft", mat: "leaf", shape: "tuft", color: nearWall ? "#a9aa8c" : "#b8ae86", cell: CELL_GRASS, x, y: gTop + th / 2 - 0.02, z, w: s, h: th, d: s, rot: sr * Math.PI });
        placed++;
      }
    }
  }
  return out;
}

/** An IVY patch: a main climbing sheet plus offset upper/side cards so the
 *  silhouette is ragged, layered at slightly different depths (no z-fight). */
function ivyPatch(
  f: Face, a: number, width: number, y0: number, yTop: number, seed: number, lowSpec: boolean,
  P: (kind: string, mat: VMat, color: string, geo: Omit<VPiece, "kind" | "mat" | "color">) => VPiece,
): VPiece[] {
  const r = rng(Math.floor(seed * 1e9) + 17);
  const g: VPiece[] = [];
  const hgt = yTop - y0;
  const tint = () => {
    const v = 0.78 + r() * 0.3;
    const c = (k: number) => Math.round(Math.min(255, 255 * v * k)).toString(16).padStart(2, "0");
    return `#${c(0.95)}${c(1)}${c(0.9)}`;
  };
  g.push(P("ivy", "leaf", tint(), cardOn(f, a, width, y0 - 0.02, y0 + hgt * 0.62, 0.08, CELL_IVY)));
  const n = lowSpec ? 1 : 3;
  for (let i = 0; i < n; i++) {
    const cw = width * (0.45 + r() * 0.35);
    const ca = a + (r() - 0.5) * (width - cw);
    const cy0 = y0 + hgt * (0.3 + r() * 0.25);
    const cy1 = Math.min(yTop, cy0 + hgt * (0.35 + r() * 0.3));
    g.push(P("ivy", "leaf", tint(), cardOn(f, ca, cw, cy0, cy1, 0.1 + i * 0.022, CELL_IVY)));
  }
  return g;
}
